<template>
    <div :class="['ufo-sec--bg', {gradient : showGradient}, setCustomClass]"
         :style="[setBackgroundImage, setOverlay, setSidebarOutline]"
         style="pointer-events: none;"
         >
        <AnimatedBackground
            v-if="showAnimatedBackground"
            ref="animatedBackground"
            :selection="section?.backgroundSettings?.animatedBackground"
            :color-palette="colorPalette"
        />

        <div v-if="showStaticBackground" class="background-wrapper">
            <InlineSvg
                v-bind:key="i"
                v-for="(layer, i) in selectedBackgroundStyle.layers"
                class="background-svg"
                :src="layer"
                :style="[getSvgColor(i)]"
                :unique-ids="`gradient-${section._id}-${i}`"
            />
        </div>
    </div>
</template>

<script>
import {EditedImageTypeEnum, HeroLayoutTypeEnum, SectionTypesEnum, SidebarBackgroundStyleEnum, SuggestedColorsEnum} from "../../common/enums.js";
import InlineSvg from "vue-inline-svg";
import bg_solid from "../../../images/background_generator/components/bg_solid.svg";
import bg_gradient_3 from "../../../images/background_generator/components/bg_gradient_3.svg";
import bg_gradient_4 from "../../../images/background_generator/components/bg_gradient_4.svg";
import bg_gradient_5 from "../../../images/background_generator/components/bg_gradient_5.svg";
import bg_gradient_6 from "../../../images/background_generator/components/bg_gradient_6.svg";
import bg_gradient_7 from "../../../images/background_generator/components/bg_gradient_7.svg";
import bg_gradient_8_a from "../../../images/background_generator/components/bg_gradient_8_a.svg";
import bg_gradient_8_b from "../../../images/background_generator/components/bg_gradient_8_b.svg";
import bg_gradient_9_a from "../../../images/background_generator/components/bg_gradient_9_a.svg";
import bg_gradient_9_b from "../../../images/background_generator/components/bg_gradient_9_b.svg";
import {generateSuggestedColors, hexToRgba} from "../../common/helpers.js";
import {mapGetters} from "vuex";
import {resolveNonHexColor} from "@/scripts/common/utilities/colorUtils.js";
import AnimatedBackground from "@/scripts/sections/common/AnimatedBackground.vue";
import animatedBackgroundPointerTunnelMixin from "@/scripts/sections/common/mixins/animatedBackgroundPointerTunnelMixin.js";

export default {
    name: 'SectionBackground',
    mixins: [animatedBackgroundPointerTunnelMixin],
    components: {
        AnimatedBackground,
        InlineSvg
    },
    props: {
        section: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            EditedImageTypeEnum: EditedImageTypeEnum,
            bgStyles: [
                {
                    id: '0',
                    thumbnail: 'bg_style_1',
                    layers: [bg_solid],
                    type: 'none'
                },
                {
                    id: '1',
                    thumbnail: 'bg_style_2',
                    layers: [bg_solid],
                    type: 'solid'
                },
                {
                    id: '2',
                    thumbnail: 'bg_style_3',
                    layers: [bg_solid, bg_gradient_3],
                    type: 'gradient'
                },
                {
                    id: '3',
                    thumbnail: 'bg_style_4',
                    layers: [bg_solid, bg_gradient_4],
                    type: 'gradient'
                },
                {
                    id: '4',
                    thumbnail: 'bg_style_5',
                    layers: [bg_solid, bg_gradient_5],
                    type: 'gradient'
                },
                {
                    id: '5',
                    thumbnail: 'bg_style_6',
                    layers: [bg_solid, bg_gradient_6],
                    type: 'gradient'
                },
                {
                    id: '6',
                    thumbnail: 'bg_style_7',
                    layers: [bg_solid, bg_gradient_7],
                    type: 'gradient'
                },
                {
                    id: '7',
                    thumbnail: 'bg_style_8',
                    layers: [bg_solid, bg_gradient_8_a, bg_gradient_8_b],
                    type: 'gradient'
                },
                {
                    id: '8',
                    thumbnail: 'bg_style_9',
                    layers: [bg_solid, bg_gradient_9_a, bg_gradient_9_b],
                    type: 'gradient'
                },
            ],
        }
    },
    computed: {
        ...mapGetters(
            "design",
            [
                "colorStyles",
                "colorPalette"
            ]
        ),
        selectedBackgroundStyle() {
            return this.bgStyles.find(e => e.id === this.section.backgroundSettings?.selectedBackgroundStyleId);
        },
        showAnimatedBackground() {
            return !!this.section?.backgroundSettings?.animatedBackground;
        },
        showStaticBackground() {
            return !this.showAnimatedBackground
                && !!this.section.backgroundSettings?.usingCustomBackground
                && this.section.backgroundSettings?.selectedBackgroundStyleId > 0
        },
        showGradient() {
            return !!(
                this.section.type === SectionTypesEnum.MAIN_HEADER &&
                this.$store.state.project.settings.displayOptions &&
                this.$store.state.project.settings.displayOptions.gradient &&
                this.$store.state.portfolio?.portfolioHero?.layoutSettings?.layoutType !== HeroLayoutTypeEnum.SIDEBAR);
        },
        setBackgroundImage() {
            let url = null;
            //TODO: legacy magic
            //if (!this.section.backgroundSettings?.backgroundColor || this.section.image && this.section.backgroundSettings?.backgroundColor && this.section.backgroundSettings.backgroundColor.length === 8) {
            if (!this.showAnimatedBackground && this.section.image && !this.section.backgroundSettings?.usingCustomBackground) {
                url = this.section.image.croppedImageUrl
                    ? this.section.image.croppedImageUrl
                    : (this.section.image.url
                        ? this.section.image.url
                        : !this.section.image.url && this.section.type === SectionTypesEnum.MEDIA
                            ? '/dist/images/empty-case-study-cover.jpg'
                            : null);
            }
            return url ? {backgroundImage: `url(${url})`} : null
        },
        setCustomClass() {
            return [this.showOverlay ? 'overlay' : null, this.showBlur ? 'blur' : null].toString().replace(',', ' ');
        },
        showOverlay() {
            const overlay = this.section.overlaySettings?.overlay;
            return !!overlay && 
                overlay.a > 0 && 
                !this.showAnimatedBackground &&
                !this.section.backgroundSettings?.usingCustomBackground;
        },
        showBlur() {
           return !!this.section.overlaySettings?.blur && 
               !this.showAnimatedBackground &&
               !this.section.backgroundSettings?.usingCustomBackground;
        },
        
        setOverlay() {
            return {'--colorOverlay': this.setDim, '--blur': this.setBlur}
        },
        setSidebarOutline() {
            if (this.section.backgroundSettings?.sidebarBackgroundStyle !== SidebarBackgroundStyleEnum.OUTLINED) return null;
            const outlineColor = this.resolveColorData(this.section.backgroundSettings?.sidebarOutlineColor);
            return {
                outline: `1px solid ${outlineColor || "transparent"}`
            };
        },
        setDim() {
            const overlay = this.section.overlaySettings?.overlay;
            if (!!overlay) {
                let rgbaColor = `rgba(${overlay.r}, ${overlay.g}, ${overlay.b}, ${overlay.a})`

                if(overlay.paletteColorIndex !== null && overlay.paletteColorIndex !== undefined) {
                    rgbaColor = hexToRgba(this.colorPalette.colors[overlay.paletteColorIndex], overlay.a)
                }

                return rgbaColor
            }
            return null
        },
        setBlur() {
            const blur = this.section.overlaySettings?.blur;
            if (!!blur) {
                return `${blur}px`
            }
            return null
        },
        sectionHeight() {
            return this.section.type === SectionTypesEnum.MAIN_HEADER
                ? !!this.section.styleSettings && !!this.section.styleSettings?.sectionHeight
                    ? `${this.section.styleSettings.sectionHeight}px`
                    : '680px'
                : null
        },
        suggestedColorsUsed() {
            const bgColor = this.section.backgroundSettings?.backgroundColor;
            return bgColor && !bgColor.startsWith('#') && Object.values(SuggestedColorsEnum).includes(bgColor)
        },
        suggestedColorOptions() {
            return generateSuggestedColors(this.colorStyles, this.colorPalette)
        },
    },
    methods: {
        resolveColorData(colorData) {
            if (!colorData) return null;
            if (Number.isFinite(colorData.paletteColorIndex)) {
                return this.colorPalette?.colors?.[colorData.paletteColorIndex] || null;
            }
            return colorData.color || null;
        },
        getSvgColor(svgIndex) {
            const { suggestedColorsUsed, selectedBackgroundStyle, section, suggestedColorOptions } = this;
            const backgroundSettings = section.backgroundSettings;

            if (suggestedColorsUsed) {
                const colorOptions = suggestedColorOptions[selectedBackgroundStyle.type][backgroundSettings?.backgroundColor];
                return { color: svgIndex === 0 ? colorOptions[0] : colorOptions[1] };
            }

            if (svgIndex === 0) return { color: resolveNonHexColor(backgroundSettings?.backgroundColor, this.colorPalette) };
            if (svgIndex === 1) return { color: resolveNonHexColor(backgroundSettings?.gradientColor1, this.colorPalette) };
            // TODO designer ignored my request, so I changed gradientColor2 to gradientColor1 here
            return { color: resolveNonHexColor(backgroundSettings?.gradientColor1, this.colorPalette) };
        }
    }

}
</script>

<style lang="scss" scoped>

.ufo-sec .ufo-sec--bg {
    overflow: hidden;
}

.background-wrapper {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .background-svg {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        transition-property: color;
        transition-duration: 200ms;
        transition-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
    }
}

.ufo-fab--container {
    top: 0;
    transform: translate(0, 100%);
    right: 72px;
    padding: 0 10px;
    visibility: hidden;
    opacity: 0;
    transition-property: opacity;
    transition-duration: 200ms;
    transition-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
    z-index: 4;
}
</style>